import { DateTime } from "luxon";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import type {
  IColumnFormatSpec,
  FormulaToken,
  IReport,
} from "./report.interface";
import type {
  IDimensionMeta,
  IMetricMeta,
  IReportingGrains,
  ReportTypeBadge,
} from "./hooks/useReportingMetadata";

dayjs.extend(utc);

export const timestampDimensionKeys = (
  catalogue: IDimensionMeta[]
): Set<string> =>
  new Set(catalogue.filter((d) => d.isTimestamp).map((d) => d.key));

export const formatTimestampUtc = (value: unknown): string => {
  if (value === null || value === undefined || value === "") return "";
  const parsed = dayjs.utc(value as string);
  if (!parsed.isValid()) return String(value);
  return parsed.format("YYYY-MM-DD HH:mm:ss");
};

/**
 * Mirrors the backend's keys. A metric missing from these sets is inferred to be a calculated
 * metric and lands in the wrong family, so keep them in step when the backend adds one.
 */
export const IMPRESSION_FREQUENCY_METRIC = "impression_frequency";
export const REACH_METRIC = "reach";

/** Both are capped server-side; the picker enforces the same bound so the run never starts. */
export const RAW_PIXEL_ONLY_MAX_RANGE_DAYS = 31;

// Their distinct count has no rollup twin, so selecting either forces a raw adte_pixel scan.
export const RAW_PIXEL_ONLY_METRICS = new Set([
  IMPRESSION_FREQUENCY_METRIC,
  REACH_METRIC,
]);

/**
 * The cap message, or null when the selection is allowed.
 *
 * Derived from the current selection rather than raised on submit, so picking the metric and
 * picking the range are the same mistake whichever order they happen in.
 */
export const rawPixelOnlyRangeError = (
  metrics: Array<{ eventType: string; label?: string }>,
  startDate: string | null | undefined,
  endDate: string | null | undefined
): string | null => {
  const capped = metrics
    .filter((m) => RAW_PIXEL_ONLY_METRICS.has(m.eventType))
    .map((m) => m.label || m.eventType);
  if (capped.length === 0 || !startDate || !endDate) return null;

  const start = new Date(startDate).getTime();
  const end = new Date(endDate).getTime();
  if (Number.isNaN(start) || Number.isNaN(end)) return null;

  const spanDays = Math.round((end - start) / 86400000);
  if (spanDays <= RAW_PIXEL_ONLY_MAX_RANGE_DAYS) return null;
  return (
    `${capped.join(", ")} can only be reported over ` +
    `${RAW_PIXEL_ONLY_MAX_RANGE_DAYS} days or fewer — this range is ${spanDays} days.`
  );
};

// Frequency lands between 2 and 8; whole numbers would hide the differences that matter.
export const FREQUENCY_FORMAT = { decimals: 2 };

/** Reach is a count of identities — decimals on it would imply a precision it does not have. */
export const REACH_FORMAT = { decimals: 0 };

export const PIXEL_DATE_METRICS = new Set([
  "served_impression_count",
  "matched_impression_count",
  "revenue",
  "cost",
  "cpm",
  "rpm",
  IMPRESSION_FREQUENCY_METRIC,
  REACH_METRIC,
]);
export const ADVERTISER_EVENT_METRICS = new Set(["advertiser_event_count"]);
const EVENT_METRIC_PREFIX = "event::";
export const eventMetricKey = (eventName: string): string =>
  `${EVENT_METRIC_PREFIX}${String(eventName || "")
    .trim()
    .toLowerCase()}`;
export const isEventMetricKey = (metricKey: string): boolean =>
  metricKey.startsWith(EVENT_METRIC_PREFIX);
export const eventNameFromMetricKey = (metricKey: string): string =>
  isEventMetricKey(metricKey)
    ? metricKey.slice(EVENT_METRIC_PREFIX.length)
    : "";
// Predicted metrics are per event, exactly like attributed event metrics: one pickable metric per
// event rather than one abstract metric whose meaning depends on what else is selected.
const PREDICTED_METRIC_PREFIX = "predicted::";
export const predictedMetricKey = (eventName: string): string =>
  `${PREDICTED_METRIC_PREFIX}${String(eventName || "")
    .trim()
    .toLowerCase()}`;
export const isPredictedMetricKey = (metricKey: string): boolean =>
  metricKey.startsWith(PREDICTED_METRIC_PREFIX);
/**
 * What a report of nothing but predictions is measured against. Predicted metrics are served by a
 * separate read, so on their own they ask the query for no columns at all — and a prediction with
 * nothing beside it has no scale to be read against, on the chart or in the grid. The cheapest metric
 * there is, so anchoring to it costs nothing.
 */
export const PREDICTION_ANCHOR_METRIC = "served_impression_count";

export const eventNameFromPredictedMetricKey = (metricKey: string): string =>
  isPredictedMetricKey(metricKey)
    ? metricKey.slice(PREDICTED_METRIC_PREFIX.length)
    : "";

const predictedEventSlug = (eventName: string): string =>
  String(eventName || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "") || "unknown";

// Must match the backend's column naming exactly, including the slug, or the column arrives unlabelled.
export const predictedColumnKeyFromEventName = (eventName: string): string =>
  `predicted_${predictedEventSlug(eventName)}`;
export const predictedRemainingColumnKeyFromEventName = (
  eventName: string
): string => `predicted_remaining_${predictedEventSlug(eventName)}`;
export const predictedErrorColumnKeyFromEventName = (
  eventName: string
): string => `predicted_error_pct_${predictedEventSlug(eventName)}`;

/**
 * How much error a reader should tolerate before treating the number as directional rather than
 * usable. Business thresholds, not statistical ones: the model has no opinion on where "good" ends.
 */
export const PREDICTION_ERROR_GOOD_PCT = 15;
export const PREDICTION_ERROR_BAD_PCT = 25;

export type PredictionErrorBand = "good" | "warn" | "bad";

export const predictionErrorBand = (
  value: unknown
): PredictionErrorBand | null => {
  const pct =
    typeof value === "number" ? value : Number.parseFloat(String(value ?? ""));
  if (!Number.isFinite(pct)) return null;
  if (pct < PREDICTION_ERROR_GOOD_PCT) return "good";
  if (pct <= PREDICTION_ERROR_BAD_PCT) return "warn";
  return "bad";
};

/** Read at a glance on the chart, matching the grid's colouring of the same figure. */
export const PREDICTION_BAND_COLOR: Record<PredictionErrorBand, string> = {
  good: "#4caf50",
  warn: "#ffa726",
  bad: "#ef5350",
};

export const eventColumnKeyFromEventName = (eventName: string): string => {
  const slug = String(eventName || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
  return `event_${slug || "unknown"}`;
};

export const getDateDimensionLabel = (
  metrics: Array<{ eventType: string }>,
  reportType?: "pixel" | "advertiser"
): string => {
  if (reportType === "advertiser") return "Conversion Date";
  if (reportType === "pixel") return "Impression Date";
  const hasAdvertiser = metrics.some((m) =>
    ADVERTISER_EVENT_METRICS.has(m.eventType)
  );
  return hasAdvertiser ? "Conversion Date" : "Impression Date";
};

// event_date is in no rollup grain, so leaving it unmapped makes the estimate read slow.
const effectiveReportDimensions = (report: IReport): string[] => {
  const userSelectedDimensions = report.dimensions.map((d) => d.key);
  const shouldAutoAddPixelDate =
    report.timeAggregation &&
    report.timeAggregation !== "Sum" &&
    !userSelectedDimensions.includes("pixel_date");
  const groupingDimensions = shouldAutoAddPixelDate
    ? ["pixel_date", ...userSelectedDimensions]
    : userSelectedDimensions;
  const filterDimensions = (report.filters || [])
    .filter((f) => f.dimension && f.specTarget && f.specTarget.length > 0)
    .map((f) => f.dimension);
  return [...groupingDimensions, ...filterDimensions];
};

const reportRangeDays = (report: IReport): number => {
  if (!report.startDate || !report.endDate) return 0;
  const start = dayjs(report.startDate);
  const end = dayjs(report.endDate);
  if (!start.isValid() || !end.isValid()) return 0;
  return Math.abs(end.diff(start, "day"));
};

/** The metrics the served rollups can answer on their own. */
const SERVED_FAMILY_METRICS = new Set(["served_impression_count", "revenue", "cost", "cpm", "rpm"]);

/**
 * The read path the server will take, mirroring its routing: attributed or calculated columns always
 * read the attribution detail table (the daily attribution aggregate only backs matched impressions,
 * which aren't selectable here), and only served-family reports can stay on a rollup.
 */
type ReportReadPath = "conversion" | "attribution" | "viewer" | "rawPixel" | "servedRollup";

const reportReadPath = (report: IReport, grains: IReportingGrains): ReportReadPath => {
  if (report.reportType === "advertiser") return "conversion";
  const keys = report.metrics.map((m) => m.eventType);
  if (keys.some((k) => ADVERTISER_EVENT_METRICS.has(k))) return "conversion";
  const attribution = keys.some(
    (k) => isEventMetricKey(k) || k === "matched_impression_count" || (!SERVED_FAMILY_METRICS.has(k) && !RAW_PIXEL_ONLY_METRICS.has(k) && !isPredictedMetricKey(k))
  );
  if (attribution) return "attribution";
  if (keys.some((k) => RAW_PIXEL_ONLY_METRICS.has(k))) return "viewer";
  const rollup = new Set(grains.servedRollup);
  return effectiveReportDimensions(report).every((d) => rollup.has(d)) ? "servedRollup" : "rawPixel";
};

/**
 * Dimensions that keep this selection on a rollup. Only served-family reports have one, so any
 * attributed event, Reach or Frequency column means no dimension is faster than another.
 */
export const fastDimensionKeys = (
  report: IReport,
  grains: IReportingGrains
): string[] => {
  const path = reportReadPath(report, grains);
  if (path !== "servedRollup" && path !== "rawPixel") return [];
  return grains.servedRollup.filter((k) => k !== "pixel_date" && k !== "event_date" && k !== "event");
};

export interface ReportSpeedEstimate {
  level: "fast" | "moderate" | "slow";
  label: string;
  estimate: string;

  score: number;
}

/**
 * Seconds by read path, fitted on mb over 7 to 90 days (Oct 2026, cold cache, totals and row count
 * included, which is what a run waits for). Attribution grows faster than linear with the range.
 */
const PATH_SECONDS: Record<ReportReadPath, (days: number) => number> = {
  servedRollup: (days) => 1.5 + 0.06 * days,
  rawPixel: (days) => 2 + 3 * days,
  viewer: (days) => 2 + 4 * days,
  attribution: (days) => 0.45 * Math.pow(days, 1.4),
  conversion: (days) => 3 + 0.3 * days,
};

/** Both dates fan out across the attribution window. Unmeasured, so kept from the previous model. */
const CROSS_AXIS_DATE_FACTOR = 4;

const crossAxisDateFactor = (report: IReport): number => {
  const keys = new Set(report.dimensions.map((d) => d.key));
  return keys.has("pixel_date") && keys.has("event_date")
    ? CROSS_AXIS_DATE_FACTOR
    : 1;
};

/** A constraint re-runs the joined result for the row count and totals; measured 2.3x on a 15-dimension mb report, flat on the rollup. */
const CONSTRAINT_FACTOR = 2.3;

const constraintFactor = (report: IReport, path: ReportReadPath): number =>
  path !== "servedRollup" && (report.constraints || []).some((c) => !!c.metric && c.value !== "")
    ? CONSTRAINT_FACTOR
    : 1;

export const estimateReportSpeed = (
  report: IReport,
  grains: IReportingGrains,
  _metricCatalogue: IMetricMeta[]
): ReportSpeedEstimate => {
  const days = Math.max(1, reportRangeDays(report) + 1);
  const path = reportReadPath(report, grains);
  const seconds = PATH_SECONDS[path](days) * crossAxisDateFactor(report) * constraintFactor(report, path);

  if (seconds < 10) return { level: "fast", label: "Fast", estimate: "≈ seconds", score: 0 };
  if (seconds < 30) return { level: "moderate", label: "Moderate", estimate: "≈ under 30 seconds", score: 0.2 };
  if (seconds < 60) return { level: "moderate", label: "Moderate", estimate: "≈ under a minute", score: 0.35 };
  if (seconds < 180) return { level: "slow", label: "Slow", estimate: "≈ 1–3 minutes", score: 0.65 };
  if (seconds < 300) return { level: "slow", label: "Slow", estimate: "≈ 3–5 minutes", score: 0.8 };
  return { level: "slow", label: "Slow", estimate: "≈ 5 minutes or more", score: 0.9 };
};

export const getDimensionOptionsForMetrics = (
  metrics: Array<{ eventType: string }>,
  catalogue: IDimensionMeta[]
): Array<{ key: string; value: string }> => {
  const family: ReportTypeBadge = metrics.some((m) =>
    ADVERTISER_EVENT_METRICS.has(m.eventType)
  )
    ? "conversion"
    : "impression";
  const hasServed = metrics.some(
    (m) => m.eventType === "served_impression_count"
  );
  return catalogue
    .filter((d) => (d.reports ?? []).includes(family))
    .filter((d) => !hasServed || !d.blockedWithServed)
    .map((d) => ({ key: d.key, value: d.label }));
};

export const getFilterDimensionOptionsForMetrics = (
  metrics: Array<{ eventType: string }>,
  dimensionOptions: Array<{ key: string; value: string }>,
  catalogue: IDimensionMeta[]
): Array<{ key: string; value: string }> => {
  const byKey = new Map(catalogue.map((d) => [d.key, d]));
  const hasServed = metrics.some(
    (m) => m.eventType === "served_impression_count"
  );

  return dimensionOptions
    .filter(
      (o) => byKey.get(o.key)?.type !== "date" && !byKey.get(o.key)?.isTimestamp
    )
    .filter((o) => !hasServed || !byKey.get(o.key)?.blockedWithServed);
};

export type Campaign = string;

export const timeOptions = ["Day", "Month", "Sum"]; // 'Hour',
export const timePresets = [
  "choose time preset",
  "Today",
  "Yesterday",
  "Last 7 days",
  "Last 30 days",
  "last calender month",
  "last calender week",
];

export const getTimeFromAggregationPresets = (
  preset: string,
  timezone = "UTC",
  now = DateTime.now()
): { startDate: string | null; endDate: string | null } => {
  const today = now.setZone(timezone).startOf("day");
  if (!today.isValid) return { startDate: null, endDate: null };
  let start = today;
  let end = today;
  switch (preset) {
    case "Today":
      break;
    case "Yesterday":
      start = end = today.minus({ days: 1 });
      break;
    case "Last 7 days":
      start = today.minus({ days: 6 });
      break;
    case "Last 30 days":
      start = today.minus({ days: 29 });
      break;
    case "last calender month":
      start = today.minus({ months: 1 }).startOf("month");
      end = start.endOf("month");
      break;
    case "last calender week":
      start = today.minus({ weeks: 1 }).startOf("week");
      end = start.endOf("week");
      break;
    default:
      return { startDate: null, endDate: null };
  }
  return { startDate: start.toISODate(), endDate: end.toISODate() };
};

// Reach and frequency are deliberately absent: a calculated metric forces the non-viewer scan
// shape, so a formula referencing a distinct count would silently cost far more than it looks.
const PIXEL_CALC_METRICS = new Set([
  "served_impression_count",
  "matched_impression_count",
  "revenue",
  "cost",
  "cpm",
  "rpm",
]);

export type CalcReportFamily = "pixel" | "advertiser" | "both";

export const inferCalcReportFamily = (formula: {
  tokens?: FormulaToken[];
}): CalcReportFamily => {
  const tokens = formula?.tokens || [];
  let hasAdvertiser = false;
  let hasPixelOrDimension = false;

  for (const tok of tokens) {
    if (tok.t !== "ref") continue;
    if (tok.kind === "metric") {
      if (tok.key === "advertiser_event_count") hasAdvertiser = true;
      else if (PIXEL_CALC_METRICS.has(tok.key)) hasPixelOrDimension = true;
    } else if (tok.kind === "sum_event") {
      hasAdvertiser = true; // event $ total => Conversion-Date family
    } else if (
      tok.kind === "event" ||
      tok.kind === "matched_event" ||
      tok.kind === "dimension"
    ) {
      hasPixelOrDimension = true;
    }
  }

  if (hasAdvertiser && !hasPixelOrDimension) return "advertiser";
  if (hasPixelOrDimension) return "pixel";
  if (hasAdvertiser) return "advertiser";
  return "both";
};

export const calcFamilyMatchesReportType = (
  family: CalcReportFamily,
  reportType: "pixel" | "advertiser"
): boolean => family === "both" || family === reportType;

export const DEFAULT_TEXT_NUMERIC_DIMENSION_FORMAT: IColumnFormatSpec = {
  decimals: 2,
};

const unwrapFormatterValue = (value: unknown): unknown =>
  value !== null && typeof value === "object" && "value" in (value as object)
    ? (value as { value: unknown }).value
    : value;

export const formatNumericDisplay = (
  value: unknown,
  spec: IColumnFormatSpec
): string => {
  const actualValue = unwrapFormatterValue(value);
  if (actualValue == null || actualValue === "") return "";
  const num =
    typeof actualValue === "number"
      ? actualValue
      : Number.parseFloat(String(actualValue));
  if (Number.isNaN(num)) return String(actualValue);
  const decimals = Math.min(6, Math.max(0, spec.decimals ?? 0));
  const prefix = spec.prefix ?? "";
  const suffix = spec.suffix ?? "";
  return `${prefix}${num.toFixed(decimals)}${suffix}`;
};
